/*
 * cm-country-selector-fix — forces correct centered positioning and real
 * scrolling on Dawn's country/currency selector panel on mobile.
 *
 * Why this exists: Dawn's mobile country selector is a `position: fixed`
 * panel positioned with percentage-based top/left/bottom values, nested
 * several levels deep inside different structures depending on where it's
 * rendered (the header's own drawer, or a plain <localization-form> on the
 * product page). Each of those contexts resolves the CSS cascade
 * differently — the header drawer in particular ends up with the panel
 * mispositioned even after neutralising the obvious transform/filter
 * containing-block issues. Rather than chase a CSS override per context,
 * this measures the real viewport directly and sets position/size via
 * inline styles once the panel is actually open, which wins over any
 * stylesheet rule regardless of which DOM context it came from.
 */
(() => {
  const MOBILE_MAX_WIDTH = 749;

  // Properties that establish a new containing block for `position: fixed`
  // descendants, and each one's reset value. Dawn's own scroll-reveal
  // animation (`scroll-trigger animate--slide-in`, applied to the whole
  // product info column that the buy-for widget lives in) leaves the
  // column with `transform: translateY(0)` once it finishes — an identity
  // transform, so it looks like a no-op, but a *set* transform still
  // creates a containing block even at zero offset. That silently turns
  // the info column into the fixed panel's containing block instead of the
  // viewport, so every "centered" coordinate this script computes actually
  // lands relative to that column — which is why the panel appeared to
  // track the button itself rather than centering in the screen.
  const CONTAINING_BLOCK_PROPS = {
    transform: 'none',
    filter: 'none',
    'backdrop-filter': 'none',
    perspective: 'none',
    'will-change': 'auto',
  };

  const createsContainingBlock = (cs) =>
    cs.transform !== 'none' ||
    cs.filter !== 'none' ||
    cs.backdropFilter !== 'none' ||
    cs.perspective !== 'none' ||
    /transform|filter|perspective/.test(cs.willChange) ||
    (cs.contain !== 'none' && /layout|paint|strict|content/.test(cs.contain));

  // Dawn's scroll-reveal effect (the `scroll-trigger animate--slide-in`
  // classes on the product info column) runs as a Web Animations API
  // animation, not a plain CSS transition. Confirmed by inspecting
  // `element.getAnimations()` directly: the column still has a *finished*
  // animation object attached (fill: forwards) long after it settles at
  // `transform: none`. Browsers treat any element with an animation
  // targeting transform/filter/perspective as a containing block for fixed
  // descendants for as long as that Animation object stays attached — even
  // once it's finished and even though getComputedStyle already reports
  // the neutral value. Overriding the style property (below) doesn't undo
  // that, because the promotion is driven by the attached Animation, not
  // by the cascade-resolved value. Committing the animation's current
  // (already-settled) values into a plain inline style, then cancelling
  // it, removes the Animation object with zero visual change and clears
  // the promotion — proven by moving the panel out from under the column
  // in a live test, which snapped it to a correctly centered position.
  const cancelContainingBlockAnimations = (panel) => {
    let el = panel.parentElement;
    while (el && el !== document.body) {
      if (typeof el.getAnimations === 'function') {
        el.getAnimations().forEach((anim) => {
          try {
            anim.commitStyles();
          } catch (err) {
            /* element not renderable (e.g. display:none) — cancel is still safe */
          }
          anim.cancel();
        });
      }
      el = el.parentElement;
    }
  };

  const neutralizeContainingBlockAncestors = (panel) => {
    const touched = [];
    let el = panel.parentElement;
    while (el && el !== document.body) {
      if (createsContainingBlock(getComputedStyle(el))) {
        const prev = {};
        Object.keys(CONTAINING_BLOCK_PROPS).forEach((prop) => {
          prev[prop] = el.style.getPropertyValue(prop);
          el.style.setProperty(prop, CONTAINING_BLOCK_PROPS[prop], 'important');
        });
        touched.push({ el, prev });
      }
      el = el.parentElement;
    }
    return touched;
  };

  const restoreContainingBlockAncestors = (touched) => {
    (touched || []).forEach(({ el, prev }) => {
      Object.keys(prev).forEach((prop) => {
        if (prev[prop]) el.style.setProperty(prop, prev[prop]);
        else el.style.removeProperty(prop);
      });
    });
  };

  const positionPanel = (panel) => {
    if (window.innerWidth > MOBILE_MAX_WIDTH) return;
    // window.innerWidth/innerHeight report the LAYOUT viewport, which on
    // real mobile Safari/Chrome can be taller than what's actually on
    // screen while the address bar is mid-collapse during a scroll —
    // exactly reproducing "centers wrong depending on how far I'd
    // scrolled". visualViewport reports the space actually visible right
    // now, with its own offset from the layout viewport's origin, which is
    // what `position: fixed` is ultimately placed within.
    const vv = window.visualViewport;
    const vw = vv ? vv.width : window.innerWidth;
    const vh = vv ? vv.height : window.innerHeight;
    const offsetLeft = vv ? vv.offsetLeft : 0;
    const offsetTop = vv ? vv.offsetTop : 0;
    const width = Math.min(vw * 0.92, 460);
    const maxHeight = vh * 0.75;
    const set = (prop, value) => panel.style.setProperty(prop, value, 'important');

    // top/left as % resolve against whatever containing block this panel
    // ends up with in a given DOM context (still wrong even after
    // neutralising transform/filter ancestors in some contexts) — pixel
    // values computed from the actual visible viewport always match it
    // regardless, which is what actually fixes this rather than just
    // relocating the bug.
    set('position', 'fixed');
    set('top', `${offsetTop + vh / 2}px`);
    set('left', `${offsetLeft + vw / 2}px`);
    set('right', 'auto');
    set('bottom', 'auto');
    set('margin', '0');
    set('transform', 'translate(-50%, -50%)');
    set('width', `${width}px`);
    set('height', 'auto');
    set('max-height', `${maxHeight}px`);
    set('overflow-y', 'auto');
    set('-webkit-overflow-scrolling', 'touch');

    const list = panel.querySelector('.country-selector__list');
    if (list) {
      list.style.setProperty('max-height', 'none', 'important');
      list.style.setProperty('overflow', 'visible', 'important');
    }
  };

  const clearPanel = (panel) => {
    ['position', 'inset', 'top', 'left', 'right', 'bottom', 'margin', 'transform', 'width', 'height', 'max-height', 'overflow-y'].forEach(
      (prop) => panel.style.removeProperty(prop)
    );
    restoreContainingBlockAncestors(panel._cmTouchedAncestors);
    panel._cmTouchedAncestors = null;
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.localization-selector');
    if (!button) return;
    // Bubble phase (no capture): Dawn's own click handler lives directly
    // on this same button and runs during the target phase, before
    // propagation reaches this document-level listener — so [hidden] is
    // already toggled to its new state by the time this callback runs.
    const form = button.closest('localization-form');
    const panel = form && form.querySelector('.disclosure__list-wrapper.country-selector');
    if (!panel) return;
    if (panel.hasAttribute('hidden')) {
      clearPanel(panel);
    } else {
      // A single synchronous measurement can land mid-flight: opening also
      // flips `body` to a scroll-locked position and (on real mobile
      // Safari) can nudge the address bar into collapsing, both of which
      // change what visualViewport reports a moment later. Re-measuring on
      // the next couple of frames/ticks catches that settle instead of
      // freezing the panel at a transient, sometimes-off-screen snapshot —
      // this is what made the box land in the wrong place "depending on
      // scroll position".
      cancelContainingBlockAnimations(panel);
      panel._cmTouchedAncestors = neutralizeContainingBlockAncestors(panel);
      positionPanel(panel);
      requestAnimationFrame(() => positionPanel(panel));
      setTimeout(() => positionPanel(panel), 300);
    }
  });

  const repositionOpenPanels = () => {
    document.querySelectorAll('.disclosure__list-wrapper.country-selector:not([hidden])').forEach(positionPanel);
  };
  window.addEventListener('resize', repositionOpenPanels);
  // The address bar collapsing/expanding fires here, not necessarily on
  // window (that only reliably fires for actual orientation/zoom changes).
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', repositionOpenPanels);
    window.visualViewport.addEventListener('scroll', repositionOpenPanels);
  }
})();
